import { Box, Button, Group, Image, Paper, RingProgress, Stack, Text } from '@mantine/core';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import { useTranslation } from '@apple/lib/i18next';
import { useBreakpoints } from '@apple/ui/layouts/hooks/useBreakpoints';

import type {
	ShipmentReportCategoryModel,
	ShipmentReportImage,
	ShipmentReportItemModel,
	ShipmentReportModel,
} from '@apple/features/store-events/models/shipment-report';
import { storeEventQueries } from '@apple/features/store-events/queries';

import classes from './shipment-report.module.css';

export const Route = createFileRoute('/_authed/store-events/$storeEventId/shipment-report')({
	params: {
		parse: x => z.object({ storeEventId: z.uuid() }).parse(x),
		stringify: ({ storeEventId }) => ({ storeEventId }),
	},
	component: ShipmentReportPage,
	wrapInSuspense: true,
});

function ShipmentReportPage() {
	const { storeEventId } = Route.useParams();
	const { data: report } = useSuspenseQuery(storeEventQueries.shipmentReport(storeEventId));

	const handlePrint = () => {
		window.print();
	};

	return (
		<Box className={classes.shipmentReport}>
			<Box className={classes.mainContainer}>
				<FirstPage report={report} onPrint={handlePrint} />

				<Box className={classes.categoriesSection}>
					{[...report.categories]
						.sort((a, b) => {
							const aIsEmk = a.name.toUpperCase().startsWith('EMK');
							const bIsEmk = b.name.toUpperCase().startsWith('EMK');
							if (aIsEmk && !bIsEmk) return -1;
							if (!aIsEmk && bIsEmk) return 1;
							return a.name.localeCompare(b.name);
						})
						.map(category => (
							<CategorySection
								key={category.name}
								category={category}
								defaultImage={report.defaultImage}
							/>
						))}
				</Box>
			</Box>
		</Box>
	);
}

interface FirstPageProps {
	report: ShipmentReportModel;
	onPrint: () => void;
}

function FirstPage({ report, onPrint }: FirstPageProps) {
	const { t } = useTranslation('store-event');

	return (
		<Box className={classes.firstPage}>
			<ReportHeader
				openingDate={report.openingDate}
				addressLineOne={report.addressLineOne}
				addressLineTwo={report.addressLineTwo}
				city={report.city}
				state={report.state}
				postalCode={report.postalCode}
				countryCode={report.countryCode}
				onPrint={onPrint}
				storeName={report.storeName}
				storeCode={report.storeCode}
			/>

			<ReportCover
				percentShipped={report.percentShipped}
				fullKitShipDate={report.fullKitShipDate}
				fullKitDeliveryDate={report.fullKitDeliveryDate}
				earlyManagementKitShipDate={report.earlyManagementKitShipDate}
				earlyManagementKitDeliveryDate={report.earlyManagementKitDeliveryDate}
			/>

			<Text size='xxs' className={classes.generatedDate}>
				<Text component='span'>{t('shipmentReport.generatedDate')}:</Text>{' '}
				{report.generatedDate}
			</Text>
		</Box>
	);
}

interface ReportHeaderProps {
	openingDate: string | null;
	addressLineOne: string;
	addressLineTwo: string | null;
	city: string;
	state: string;
	postalCode: string;
	countryCode: string;
	storeName: string;
	storeCode: string;
	onPrint: () => void;
}

function ReportHeader({
	openingDate,
	addressLineOne,
	addressLineTwo,
	city,
	state,
	postalCode,
	countryCode,
	onPrint,
	storeName,
	storeCode,
}: ReportHeaderProps) {
	const { t } = useTranslation('store-event');

	return (
		<Box className={classes.header} p='md'>
			<Group justify='space-between' align='flex-start' wrap='nowrap'>
				<Stack>
					<Text className={classes.title}>{t('shipmentReport.appleRetail')}</Text>
					<Text className={classes.storeName}>
						{storeCode} - {storeName}
					</Text>
				</Stack>
				<Stack gap='xs' align='flex-end'>
					<Button onClick={onPrint} className={classes.printButton}>
						{t('shipmentReport.print')}
					</Button>
					<Text className={classes.openDate}>
						<Text component='span' fw={700} inherit>
							{t('shipmentReport.opening')}
						</Text>{' '}
						{openingDate}
					</Text>
					<Box className={classes.address}>
						<Text>{addressLineOne}</Text>
						{addressLineTwo && <Text>{addressLineTwo}</Text>}
						<Text>
							{city}, {state} {postalCode}
						</Text>
						<Text>{countryCode}</Text>
					</Box>
				</Stack>
			</Group>
		</Box>
	);
}

interface ReportCoverProps {
	percentShipped: number;
	fullKitShipDate: string | null;
	fullKitDeliveryDate: string | null;
	earlyManagementKitShipDate: string | null;
	earlyManagementKitDeliveryDate: string | null;
}

function ReportCover({
	percentShipped,
	fullKitShipDate,
	fullKitDeliveryDate,
	earlyManagementKitShipDate,
	earlyManagementKitDeliveryDate,
}: ReportCoverProps) {
	const { t } = useTranslation('store-event');

	return (
		<Box className={classes.cover} p='md' mt='md'>
			<Group align='center' justify='center' gap='xl' mb='lg'>
				<RingProgress
					size={220}
					thickness={24}
					roundCaps
					sections={[
						...(percentShipped < 100
							? [{ value: 100 - percentShipped, color: 'gray.3' }]
							: []),
						{ value: percentShipped, color: 'teal' },
					]}
				/>
				<Box>
					<Text className={classes.percentText}>{percentShipped.toFixed(2)}%</Text>
					<Text className={classes.shippedLabel}>{t('labels.shipped')}</Text>
				</Box>
			</Group>

			<Group justify='center' gap='xl'>
				<Stack gap='xs'>
					<Text size='sm'>
						<Text component='span' fw={700}>
							{t('shipmentReport.mainKitDeliveryDate')}:
						</Text>{' '}
						{fullKitDeliveryDate}
					</Text>
					<Text size='sm'>
						<Text component='span' fw={700}>
							{t('shipmentReport.kitShipDate')}:
						</Text>{' '}
						{fullKitShipDate}
					</Text>
				</Stack>
				<Stack gap='xs'>
					<Text size='sm'>
						<Text component='span' fw={700}>
							{t('shipmentReport.emkDeliveryDate')}:
						</Text>{' '}
						{earlyManagementKitDeliveryDate}
					</Text>
					<Text size='sm'>
						<Text component='span' fw={700}>
							{t('shipmentReport.emkShipDate')}:
						</Text>{' '}
						{earlyManagementKitShipDate}
					</Text>
				</Stack>
			</Group>
		</Box>
	);
}

interface CategorySectionProps {
	category: ShipmentReportCategoryModel;
	defaultImage: ShipmentReportImage;
}

function CategorySection({ category, defaultImage }: CategorySectionProps) {
	return (
		<Box>
			{category.items.map((item, index) => (
				<Box key={`${item.partNumber}-${index}`}>
					{index % 9 === 0 && (
						<CategoryHeader
							name={category.name}
							labelImageUrl={category.labelImageUrl}
						/>
					)}
					<CategoryItem
						item={item}
						defaultImage={defaultImage}
						highlight={item.quantityOrdered > item.quantityShipped}
					/>
				</Box>
			))}
		</Box>
	);
}

interface CategoryHeaderProps {
	name: string;
	labelImageUrl: string | null;
}

function CategoryHeader({ name, labelImageUrl }: CategoryHeaderProps) {
	const { t } = useTranslation('store-event');

	return (
		<Box className={classes.categoryHeader} p='md' mt='md'>
			<Group justify='space-between' align='center'>
				<Text size='lg'>
					<Text component='span' fw={700}>
						{t('shipmentReport.category')}:
					</Text>{' '}
					{name}
				</Text>
				{labelImageUrl && (
					<Box className={classes.categoryLabelImageWrapper}>
						<Image
							src={labelImageUrl}
							alt={name}
							className={classes.categoryLabelImage}
						/>
					</Box>
				)}
			</Group>
		</Box>
	);
}

interface CategoryItemProps {
	item: ShipmentReportItemModel;
	defaultImage: ShipmentReportImage;
	highlight: boolean;
}

function CategoryItem({ item, defaultImage, highlight }: CategoryItemProps) {
	const { t } = useTranslation('store-event');
	const itemImage = item.images.find(img => img.sortOrder === 0) ?? defaultImage;
	const { smOrSmaller } = useBreakpoints();

	return (
		<Paper
			className={classes.categoryItem}
			p={4}
			mx={4}
			my={2}
			data-highlight={highlight || undefined}
		>
			<Group align='flex-start' gap='md' wrap='nowrap'>
				<Box className={classes.itemImage}>
					<Image
						src={itemImage.largeImageUrl}
						alt={item.name}
						fit='contain'
						h={smOrSmaller ? 64 : 75}
						w={smOrSmaller ? 64 : 75}
					/>
				</Box>
				<Box className={classes.itemDescription} style={{ flex: 1 }}>
					<Text className={classes.itemTitle}>
						<Text component='span' fw={700}>
							{item.partNumber}:
						</Text>{' '}
						{item.name}
					</Text>
					<Group gap='md' mt='xs'>
						<Text size='sm'>
							<Text component='span' c='teal'>
								{t('shipmentReport.unit')}:
							</Text>{' '}
							{item.unit}
						</Text>
						<Text size='sm'>
							<Text component='span' c='teal'>
								{t('shipmentReport.ordered')}:
							</Text>{' '}
							{item.quantityOrdered}
						</Text>
						<Text size='sm'>
							<Text component='span' c='teal'>
								{t('shipmentReport.shipped')}:
							</Text>{' '}
							{item.quantityShipped}
						</Text>
					</Group>
					{(item.carriers || item.trackingNumbers || item.comment) && (
						<Stack gap={2} mt='xs'>
							{item.carriers && (
								<Text size='sm'>
									<Text component='span' c='teal'>
										{item.carriers.includes(',')
											? t('shipmentReport.carriers')
											: t('shipmentReport.carrier')}
										:
									</Text>{' '}
									{item.carriers}
								</Text>
							)}
							{item.trackingNumbers && (
								<Text size='sm' className={classes.trackingInfo}>
									<Text component='span' c='teal'>
										{t('shipmentReport.trackingInfo')}:
									</Text>{' '}
									{item.trackingNumbers}
								</Text>
							)}
							{item.comment && (
								<Text size='sm' className={classes.comment}>
									<Text component='span' c='teal'>
										{t('shipmentReport.comment')}:
									</Text>{' '}
									{item.comment}
								</Text>
							)}
						</Stack>
					)}
				</Box>
				<Box className={classes.receivedBox}>
					<Stack gap={5}>
						<Paper withBorder className={classes.receivedBoxInner} />
						<Text size='xs' fw={600} ta='center'>
							{t('shipmentReport.received')}
						</Text>
					</Stack>
				</Box>
			</Group>
		</Paper>
	);
}
